import { createBrowserRouter, Navigate, useParams, useLocation } from 'react-router-dom'
import { lazy } from 'react'
import type { RouteObject } from 'react-router-dom'
import { processRoutes } from './route-utils'
import { authRoutes } from '../features/auth/auth.routes'
import AppLayout from '../components/AppLayout'
import { Error403, Error404, Error500 } from '@shopmuchmore/design-system'
import { RouterErrorBoundary } from './RouterErrorBoundary'

const HomePage = lazy(() => import('../features/home/pages/HomePage').then((m: any) => ({ default: m.HomePage || m.default })))

const ProductDetailPage = lazy(() => import('../features/product/pages/ProductDetailPage').then((m: any) => ({ default: m.ProductDetailPage || m.default })))
const AboutUsPage = lazy(() => import('../features/static/pages/AboutUsPage').then((m: any) => ({ default: m.AboutUsPage || m.default })))
const ContactUsPage = lazy(() => import('../features/static/pages/ContactUsPage').then((m: any) => ({ default: m.ContactUsPage || m.default })))
const TermsAndConditionsPage = lazy(() => import('../features/static/pages/TermsAndConditionsPage').then((m: any) => ({ default: m.TermsAndConditionsPage || m.default })))
const PrivacyPolicyPage = lazy(() => import('../features/static/pages/PrivacyPolicyPage').then((m: any) => ({ default: m.PrivacyPolicyPage || m.default })))
const DataDeletionPage = lazy(() => import('../features/static/pages/DataDeletionPage').then((m: any) => ({ default: m.DataDeletionPage || m.default })))
const MerchantManualPage = lazy(() => import('../features/static/pages/MerchantManualPage').then((m: any) => ({ default: m.MerchantManualPage || m.default })))
const ReturnAndCancellationPolicyPage = lazy(() => import('../features/static/pages/ReturnAndCancellationPolicyPage').then((m: any) => ({ default: m.ReturnAndCancellationPolicyPage || m.default })))

const MerchantPage = lazy(() => import('../features/merchant/pages/MerchantPage').then((m: any) => ({ default: m.MerchantPage || m.default })))
const NearbyMerchantsPage = lazy(() => import('../features/merchant/pages/NearbyMerchantsPage').then((m: any) => ({ default: m.NearbyMerchantsPage || m.default })))
const BrowsePage = lazy(() => import('../features/browse/pages/BrowsePage').then((m: any) => ({ default: m.BrowsePage || m.default })))
const CartPage = lazy(() => import('../features/cart/pages/CartPage').then((m: any) => ({ default: m.CartPage || m.default })))
const CheckoutStatusPage = lazy(() => import('../features/checkout/pages/CheckoutStatusPage').then((m: any) => ({ default: m.CheckoutStatusPage || m.default })))
import { accountRoutes } from '../features/account/account.routes'
import { ProtectedRoute } from '../features/auth/components/ProtectedRoute'

const OrderRedirector = () => {
    const { orderNumber } = useParams();
    return <Navigate to={`/account/orders/${orderNumber}`} replace />;
};

const BrowseRedirector = () => {
    const { slug } = useParams();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    if (slug) {
        searchParams.set('slug', slug);
    }
    return <Navigate to={`/browse?${searchParams.toString()}`} replace />;
};

// Combine all feature routes (protected routes)
const appRoutes: RouteObject[] = []

// Process routes to automatically apply role protection based on handle.roles
const processedAppRoutes = processRoutes(appRoutes)

// Main router configuration - all routes wrapped with AppLayout
export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'merchant/:merchantId',
        element: <MerchantPage />,
      },
      {
        path: 'nearby-merchants',
        element: <NearbyMerchantsPage />,
      },
      {
        path: 'merchants/nearby',
        element: <Navigate to="/nearby-merchants" replace />,
      },
      {
        path: 'products/:slug',
        element: <ProductDetailPage />,
      },
      {
        path: 'browse',
        element: <BrowsePage />,
      },
      {
        path: 'browse/:slug',
        element: <BrowseRedirector />,
      },
      {
        path: 'cart',
        element: <CartPage />,
      },
      {
        path: 'checkout/redirect/:orderId',
        element: (
          <ProtectedRoute>
            <CheckoutStatusPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'order/:orderNumber',
        element: (
          <ProtectedRoute>
            <OrderRedirector />
          </ProtectedRoute>
        ),
      },
      {
        path: 'about',
        element: <AboutUsPage />,
      },
      {
        path: 'contact',
        element: <ContactUsPage />,
      },
      {
        path: 'terms-and-conditions',
        element: <TermsAndConditionsPage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPolicyPage />,
      },
      {
        path: 'data-deletion',
        element: <DataDeletionPage />,
      },
      {
        path: 'merchant-manual',
        element: <MerchantManualPage />,
      },
      {
        path: 'return-and-cancellation-policy',
        element: <ReturnAndCancellationPolicyPage />,
      },
      {
        path: 'returns',
        element: <ReturnAndCancellationPolicyPage />,
      },
      {
        path: 'refunds',
        element: <ReturnAndCancellationPolicyPage />,
      },
      ...processedAppRoutes,
      ...authRoutes,
      ...accountRoutes,
      {
        path: '/404',
        element: <Error404 />,
      },
      {
        path: '/403',
        element: <Error403 />,
      },
      {
        path: '/500',
        element: <Error500 />,
      },
      {
        path: '*',
        element: <Error404 />,
      },
    ],
  },
], {
  future: {
    v7_relativeSplatPath: true,
    v7_startTransition: true,
  } as any,
})
